import { axiosTassoQuintoApiInstance } from '../tasso-quinto';
import { AcquisitionOperation } from './acquisitionOperation';

/**
 * The products whose acquisition is a resumable operation. Mutuo is absent on
 * purpose: it keeps its accepted-response semantics and has no operation
 * registry to recover from.
 *
 * The identifier is the path segment itself, because the recovery routes are
 * product-scoped: the same operation id under the wrong product is not that
 * operation, it is a `404`.
 */
export type AcquisitionOperationProduct = 'prestiti' | 'cessioni-del-quinto';

const operationPath = (
  product: AcquisitionOperationProduct,
  operationId: string
) => `/v1/backoffice/${product}/acquisition-operations/${operationId}`;

/**
 * Reads back an operation the browser only knows by its identifier. It is the
 * one call that needs no `Idempotency-Key`: it writes nothing and resumes
 * nothing, so there is no command for a key to name.
 *
 * Recovery is scoped to the product in the path and to the real actor of the
 * token, so an unknown operation, another product, another actor, an unmatched
 * key or an operation past the backend's retention all answer the same
 * `404 acquisition_operation_not_found`.
 */
export const getAcquisitionOperation = async (
  product: AcquisitionOperationProduct,
  operationId: string
): Promise<AcquisitionOperation> => {
  const { data } = await axiosTassoQuintoApiInstance.get<AcquisitionOperation>(
    operationPath(product, operationId)
  );
  return data;
};

/**
 * Asks the backend to finish the steps an incomplete operation left pending.
 * Retry resumes from identifiers, never from a command: the original key names
 * the work to continue and the body stays empty, because any field in it is a
 * `400 invalid_request` rather than a second version of the command.
 */
export const retryAcquisitionOperation = async (
  product: AcquisitionOperationProduct,
  operationId: string,
  idempotencyKey: string
): Promise<AcquisitionOperation> => {
  const { data } = await axiosTassoQuintoApiInstance.post<AcquisitionOperation>(
    `${operationPath(product, operationId)}/retry`,
    undefined,
    { headers: { 'Idempotency-Key': idempotencyKey } }
  );
  return data;
};
